import { venueApi } from './api'
import { useDataRead, DataHookOptions, useDataList } from '@hooks/data'
import { Venue, VenueDaySettings } from 'types/model'
import { AppConfig } from 'types/model/appConfig'
import {
  VenueLayout,
  VenueLayoutSection,
  VenueLayoutTable,
} from 'types/model/venue-layout'
import { FloorplanSchemaVersion } from 'shared'

function getPath(append?: number | string) {
  return append ? venueApi.pathPrefix.concat(`${append}`) : venueApi.pathPrefix
}

export function useVenue(
  venue: string | null,
  options?: DataHookOptions<Venue>
) {
  const appConfigOptions = options as DataHookOptions<AppConfig> | undefined
  const path = venue ? getPath(`/${venue}/app-config`) : null
  const fallbackVenue = options?.config?.fallbackData
  const appConfigResponse = useDataRead<AppConfig>(venueApi, path, {
    ...appConfigOptions,
    config: {
      ...appConfigOptions?.config,
      fallbackData:
        fallbackVenue !== undefined
          ? ({
              venue: fallbackVenue,
              integrations: [],
            } as AppConfig)
          : undefined,
      revalidateOnMount:
        appConfigOptions?.config?.revalidateOnMount ??
        (fallbackVenue !== undefined ? false : undefined),
      revalidateOnFocus: appConfigOptions?.config?.revalidateOnFocus ?? false,
    },
  })

  return {
    ...appConfigResponse,
    data: appConfigResponse.data?.venue,
  }
}

export function useAppConfig(
  venue: string,
  options?: DataHookOptions<AppConfig>
) {
  return useDataRead<AppConfig>(
    venueApi,
    getPath(`/${venue}/app-config`),
    options
  )
}

export function useVenueDaySettings(
  venue: string | null,
  dayId: string | null,
  options?: DataHookOptions<VenueDaySettings>
) {
  const path =
    venue && dayId ? getPath(`/${venue}/day-settings/${dayId}`) : null
  return useDataRead<VenueDaySettings>(venueApi, path, options)
}

export function useVenueDaySettingsList(
  venue: string | null,
  options?: DataHookOptions<VenueDaySettings>
) {
  const path = venue ? getPath(`/${venue}/day-settings`) : null
  return useDataRead<VenueDaySettings>(venueApi, path, options)
}

export function useVenueLayouts(
  venue: string | null,
  options?: DataHookOptions<VenueLayout[]>
) {
  const path = venue ? getPath(`/${venue}/layouts`) : null
  const layoutParams = {
    ...(options?.params as Record<string, unknown> | undefined),
    floorplanSchemaVersion: FloorplanSchemaVersion.LEGACY,
  }

  return useDataList<VenueLayout>(venueApi, path, {
    ...options,
    params: layoutParams as DataHookOptions<VenueLayout[]>['params'],
  })
}

export function useVenueSections(
  venue: string | null,
  options?: DataHookOptions<VenueLayoutSection[]>
) {
  const path = venue ? getPath(`/${venue}/layout-sections`) : null
  return useDataList<VenueLayoutSection>(venueApi, path, options)
}

export function useVenueTables(
  venue: string | null,
  options?: DataHookOptions<VenueLayoutTable[]>
) {
  const path = venue ? getPath(`/${venue}/layout-tables`) : null
  return useDataList<VenueLayoutTable>(venueApi, path, {
    ...options,
    params: {
      limit: 400,
      ...options?.params,
    },
  })
}
